import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbDate, NgbDatepicker, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import * as Feather from 'feather-icons';

import { AccountService } from '../../../../../../services/account/account.service';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import {
  loadScheduledEventFiltersFromGetParams,
  ScheduledEvent,
  ScheduledEventFilterBookingCapacity,
  ScheduledEventFilters,
  ScheduledEventPaymentStatus
} from '../../../../../../models/scheduled-event';
import { addFiltersToGetParams, getISO8601DateString, propExists, timeStringFromISO8601 } from '../../../../../../utils';
import {
  eventFullDueToSecondaryCapacity,
  instructorsManuallyEdited
} from '../../../../../../utils-models';
import { LocationService } from '../../../../../../services/location/location.service';
import { EventType, EventTypeCategory} from '../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { ScheduledEventsFiltersModalComponent } from './modals/scheduled-events-filters-modal/scheduled-events-filters-modal.component';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';


@Component({
  selector: 'app-events-list',
  templateUrl: './events-list.component.html',
  styleUrls: ['./events-list.component.css']
})
export class EventsListComponent implements OnInit, AfterViewInit {

  @ViewChild('datePicker') datePicker: NgbDatepicker;

  public timeFilter: string;
  public dateFilter: Date;
  public filters: ScheduledEventFilters = {}

  public reloadScheduledEventsSubject = new Subject<null>();

  public scheduledEvents: ScheduledEvent[];
  public eventTypes: EventType[];
  public eventCategories: EventTypeCategory[];

  public loadingScheduledEvents = false;
  public refreshingScheduledEvents = false;

  public loadingEventTypes = true;
  public loadingEventCategories = true;

  public serverError: number = null;

  public displayDatePicker = false;

  // Create instance references to enums to they may be called in the template.
  public EventTypeStatus = EventTypeStatus;
  public eventFullDueToSecondaryCapacity = eventFullDueToSecondaryCapacity;
  public instructorsManuallyEdited = instructorsManuallyEdited;
  public timeStringFromISO8601 = timeStringFromISO8601;

  // The number of loadScheduledEvents calls. Used to ensure that only the most
  // recent result is displayed, in the event of concurrent requests (due to,
  // e.g., rapid pagination).
  private loadScheduledEventsRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public locationService: LocationService,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private scheduledEventService: ScheduledEventService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.activatedRoute.data.subscribe(data => {
      this.timeFilter = data.timeFilter;
    });

    const routeParams = this.activatedRoute.snapshot.params;
    if (routeParams.hasOwnProperty('date')) {
      const dateParams = routeParams.date.split('-');
      if (dateParams.length < 3) {
        return;
      }
      this.dateFilter = new Date();
      this.dateFilter.setFullYear(
        parseInt(dateParams[0], 10),
        parseInt(dateParams[1], 10) - 1,
        parseInt(dateParams[2], 10)
      );
    }

    if ((this.timeFilter !== 'now') && (!this.dateFilter)) {
      this.dateFilter = new Date();
      this.navigateToDate(true);
    }

    this.reloadScheduledEventsSubject.pipe(
      // Avoid rapid requests by waiting 300ms before reloading the scheduled
      // events.
      debounceTime(300)
    ).subscribe(() => {
      this.loadScheduledEvents(true);
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute,
        ['location_id']
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = loadScheduledEventFiltersFromGetParams(this.activatedRoute);

    if (!this.activatedRoute.snapshot.queryParamMap.has('location_id')) {
      this.filters.location_id = this.accountService.getMyAccount().default_gym_id;
    }

    this.loadScheduledEvents(true);
    this.loadEventTypes();
    this.loadEventCategories();
  }

  ngAfterViewInit(): void {
    Feather.replace();
  }

  public setDatePickerDate() {
    if (!this.dateFilter || !this.datePicker) {
      return;
    }

    this.datePicker.navigateTo({
      year: this.dateFilter.getFullYear(),
      month: this.dateFilter.getMonth() + 1,
      day: this.dateFilter.getDate()
    });
  }

  public shiftDay(shiftNum: number) {
    // Update the date value and URL.
    this.dateFilter.setDate(this.dateFilter.getDate() + shiftNum);
    this.navigateToDate();

    // Close the date picker if it's open.
    this.displayDatePicker = false;

    // Display the loading state (this is so that the loading message appears
    // immediately, rather than after the debounce).
    this.loadingScheduledEvents = true;

    this.reloadScheduledEventsSubject.next();
  }

  public handleDateSelect(selectedDate: NgbDate) {
    this.dateFilter = new Date();
    this.dateFilter.setFullYear(
      selectedDate.year,
      selectedDate.month - 1,
      selectedDate.day
    );
    this.navigateToDate();
    this.loadScheduledEvents(true);
    this.displayDatePicker = false;
  }

  public toggleDisplayDatePicker() {
    this.displayDatePicker = !this.displayDatePicker;

    if (this.displayDatePicker) {
      // Allow the calendar to render before setting the date.
      setTimeout(
        () => { this.setDatePickerDate(); },
        0
      );
    }
  }

  public loadScheduledEvents(showLoading: boolean) {
    if (showLoading) {
      this.loadingScheduledEvents = true;
    } else {
      if (this.loadingScheduledEvents) { return; }
      if (this.refreshingScheduledEvents) { return; }

      this.refreshingScheduledEvents = true;
    }

    // Increment the request count and save a local copy.
    this.loadScheduledEventsRequestCount++;
    const thisRequestCount = this.loadScheduledEventsRequestCount;

    let scheduledEventsPromise: Promise<ScheduledEvent[]>;

    if (this.timeFilter === 'now') {
      scheduledEventsPromise = this.scheduledEventService
        .getCurrentScheduledEvents(this.filters);
    } else if (this.timeFilter === 'past') {
      scheduledEventsPromise = this.scheduledEventService
        .getPastScheduledEvents(getISO8601DateString(this.dateFilter), this.filters);
    } else {
      scheduledEventsPromise = this.scheduledEventService
        .getFutureScheduledEvents(getISO8601DateString(this.dateFilter), this.filters);
    }

    scheduledEventsPromise
      .then(scheduledEvents => {
        // Only update the events list if there has not been a more recent
        // request initiated.
        if (thisRequestCount !== this.loadScheduledEventsRequestCount) {
          return;
        }

        this.scheduledEvents = scheduledEvents;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        // Only update the loading/refreshing status if there has not been a
        // more recent request initiated.
        if (thisRequestCount !== this.loadScheduledEventsRequestCount) {
          return;
        }

        if (showLoading) {
          this.loadingScheduledEvents = false;
        } else {
          this.refreshingScheduledEvents = false;
        }
      });
  }

  public isIncompletePrivateEvent(scheduledEvent: ScheduledEvent) {
    if (!scheduledEvent.is_private) { return false; }
    if (scheduledEvent.is_cancelled) { return false; }
    if (scheduledEvent.reservations.length === 0) { return true; }
    return scheduledEvent.reservations[0].payment_status === ScheduledEventPaymentStatus.paymentPending;
  }

  public getActiveFilters(): string[] {
    const activeFilters: string[] = [];

    const fs = this.filters;

    if (this.locationService.getLocations() === null) {
      activeFilters.push('Loading...');
    } else if (this.locationService.getLocations().length > 1) {
      if (propExists(fs.location_id)) {
        const location = this.getLocationForId(fs.location_id);
        if (location !== null) {
          activeFilters.push('Event is located at ' + location.name);
        } else {
          activeFilters.push('Event is located at "[unknown location]"');
        }
      } else {
        activeFilters.push('Event is at any location')
      }
    }

    if (propExists(fs.event_type_id)) {
      if (this.loadingEventTypes) {
        activeFilters.push('Loading...');
      } else {
        const eventType = this.getEventTypeForId(fs.event_type_id);
        if (eventType !== null) {
          activeFilters.push('Event type is "' + eventType.name + '"');
        } else {
          activeFilters.push('Event type is "[unknown event type]"');
        }
      }
    }

    if (propExists(fs.event_category_id)) {
      if (this.loadingEventCategories) {
        activeFilters.push('Loading...');
      } else {
        const eventCategory = this.getEventCategoryForId(fs.event_category_id);
        if (eventCategory !== null) {
          activeFilters.push('Event category is "' + eventCategory.name + '"');
        } else {
          activeFilters.push('Event category is "[unknown event category]"');
        }
      }
    }

    if (propExists(fs.is_private)) {
      if (fs.is_private) {
        activeFilters.push('Event is private');
      } else {
        activeFilters.push('Event is not private');
      }
    }

    if (propExists(fs.is_cancelled)) {
      if (fs.is_cancelled) {
        activeFilters.push('Event is cancelled');
      } else {
        activeFilters.push('Event is not cancelled');
      }
    }

    if (propExists(fs.has_instructor)) {
      if (fs.has_instructor) {
        activeFilters.push('Event has an instructor');
      } else {
        activeFilters.push('Event does not have an instructor');
      }
    }

    if (propExists(fs.instructor_id)) {
      activeFilters.push('Event is instructed by ' + fs.instructor_name);
    }

    if (propExists(fs.booking_capacity)) {
      switch (fs.booking_capacity) {
        case ScheduledEventFilterBookingCapacity.full:
          activeFilters.push('Event is full');
          break;
        case ScheduledEventFilterBookingCapacity.notFull:
          activeFilters.push('Event is not full');
          break;
        case ScheduledEventFilterBookingCapacity.halfOrMoreBooked:
          activeFilters.push('Event is at least half booked');
          break;
        case ScheduledEventFilterBookingCapacity.lessThanHalfBooked:
          activeFilters.push('Event is less than half booked');
          break;
        case ScheduledEventFilterBookingCapacity.atLeastOneBooking:
          activeFilters.push('Event has at least one booking');
          break;
        case ScheduledEventFilterBookingCapacity.noBookings:
          activeFilters.push('Event has no bookings');
          break;
      }
    }

    if (propExists(fs.has_waitlist)) {
      if (fs.has_waitlist) {
        activeFilters.push('Event has participants on waitlist');
      } else {
        activeFilters.push('Event does not have participants on waitlist');
      }
    }

    return activeFilters;
  }

  public openEditFiltersModal() {
    if (this.loadingFiltersModalRequirements()) { return; }

    const modalRef = this.modalService.open(ScheduledEventsFiltersModalComponent);
    modalRef.componentInstance.inputFilters = this.filters;
    modalRef.componentInstance.eventTypes = this.eventTypes;
    modalRef.componentInstance.eventCategories = this.eventCategories;
    modalRef.componentInstance.fullBookingCapacityCalculation = true;
    modalRef.componentInstance.savedCallback = (newFilters: ScheduledEventFilters) => {
      this.filters = newFilters;
      this.reloadScheduledEventsSubject.next();
    };
  }

  public loadingFiltersModalRequirements() {
    return (
      this.loadingEventTypes ||
      this.loadingEventCategories ||
      (this.locationService.getLocations() === null)
    );
  }

  private navigateToDate(replaceUrl = false) {
    this.router.navigate(
      ['/events/scheduled/' + this.timeFilter + '/' + getISO8601DateString(this.dateFilter)],
      {replaceUrl}
    );
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(1, 3000)
      .then(response => {
        this.eventTypes = response.results
          .filter((value) => {
            return (value.status !== EventTypeStatus.draft);
          })
          .sort((a, b) => {
            if (a.status !== b.status) {
              if (a.status === EventTypeStatus.active) { return -1; }
              else { return 1; }
            }
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(1, 1000)
      .then(response => {
        this.eventCategories = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventCategories = false; });
  }

  private getEventTypeForId(eventTypeId: number) {
    for (const eventType of this.eventTypes) {
      if (eventType.id === eventTypeId) {
        return eventType;
      }
    }

    return null;
  }

  private getEventCategoryForId(eventCategoryId: number): EventTypeCategory | null {
    for (const eventCategory of this.eventCategories) {
      if (eventCategory.id === eventCategoryId) {
        return eventCategory;
      }
    }

    return null;
  }

  private getLocationForId(locationId: number) {
    for (const location of this.locationService.getLocations()) {
      if (location.id === locationId) {
        return location;
      }
    }

    return null;
  }

}
